import moment from 'moment'
import React, { useEffect } from 'react'

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { Box, Button, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { MdDeleteOutline } from 'react-icons/md'

import { Card } from '@/components/Elements'
import { Checkbox, DatePicker, Input, PriceInput, Select, VersionFileInput } from '@/components/Form'
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { OfferBookingDocumentTypes } from '@/features/offers/constants'
import { offerBookingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerBookingEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'

const BookingCommercialItem = ({
  title,
  path,
  value,
  type = 'text',
  isOther = false,
  haveDueDate = false,
  removeAction,
  disabled = false,
}) => {
  return (
    <Tr>
      <Td>{isOther ? <Input name={`${path}.label`} disabled={disabled} /> : <TooltipCell text={title} />}</Td>
      <Td>
        {type === 'text' && <Input name={`${path}.value`} disabled={disabled} isRequired={false} />}
        {type === 'number' && <PriceInput name={`${path}.value`} disabled={disabled} />}
        {type === 'yesNoDropdown' && (
          <Select
            name={`${path}.value`}
            disabled={disabled}
            options={[
              { label: '-', value: '' },
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
        )}
      </Td>
      <Td>
        {haveDueDate && value === 'yes' ? (
          <DatePicker name={`${path}.dueDate`} isRow disabled={disabled} isRequired={false} />
        ) : (
          <>-</>
        )}
      </Td>
      <Td>
        <VersionFileInput
          name={`${path}.attachments`}
          fileType={OfferBookingDocumentTypes.BOOKING_COMMERCIAL_ATTACHMENT}
          canUpload={!disabled}
          isRow
          isMultiple
        />
      </Td>
      <Td>
        <Input name={`${path}.remark`} disabled={disabled} isRequired={false} />
      </Td>
      <Td>
        <Checkbox name={`${path}.completed`} disabled={disabled} isRequired={false} />
      </Td>
      <Td>
        {isOther && (
          <Box _hover={{ cursor: 'pointer' }}>
            <MdDeleteOutline size={24} onClick={removeAction} />
          </Box>
        )}
      </Td>
    </Tr>
  )
}

const BookingTabCommercialSection = () => {
  const { setValue, getValues } = useFormContext()
  const offerPermissions = useSelector(selectOfferPermissions)

  const canEditBookingCommercialItem =
    offerPermissions[offerBookingGroupPolicyType.BOOKING_COMMERCIAL_ITEMS]?.edit ?? false

  const {
    fields: commercialOther,
    append: appendCommercialOther,
    remove: removeCommercialOther,
  } = useFieldArray({ name: 'booking.commercialChecklist.other' })
  const offerStatus = useWatch({ name: 'status' })
  const paymentTerms = useWatch({
    name: `booking.commercialChecklist.paymentTerms`,
  })
  const requireBond = useWatch({
    name: `booking.commercialChecklist.requireBond`,
  })
  const insurance = useWatch({ name: `booking.commercialChecklist.insurance` })
  const openAccountForEPD = useWatch({
    name: `booking.commercialChecklist.openAccountForEPD`,
  })
  const declareInCIC = useWatch({
    name: `booking.commercialChecklist.declareInCIC`,
  })
  const assignmentConsent = useWatch({
    name: `booking.commercialChecklist.assignmentConsent`,
  })
  const PODate = useWatch({ name: 'booking.PODate' })

  const addCommercialOther = () => {
    appendCommercialOther({
      label: '',
      value: '',
      dueDate: undefined,
    })
  }

  useEffect(() => {
    if (PODate) {
      const bondDueDate = getValues('booking.commercialChecklist.requireBond.dueDate')
      if (!bondDueDate) {
        setValue('booking.commercialChecklist.requireBond.dueDate', moment(PODate).add(30, 'day').toDate())
      }
      const insuranceDueDate = getValues('booking.commercialChecklist.insurance.dueDate')
      if (!insuranceDueDate) {
        setValue('booking.commercialChecklist.insurance.dueDate', moment(PODate).add(30, 'day').toDate())
      }
      const openAccountForEPDDueDate = getValues('booking.commercialChecklist.openAccountForEPD.dueDate')
      if (!openAccountForEPDDueDate) {
        setValue('booking.commercialChecklist.openAccountForEPD.dueDate', moment(PODate).add(21, 'day').toDate())
      }
      const declareInCICDueDate = getValues('booking.commercialChecklist.declareInCIC.dueDate')
      if (!declareInCICDueDate) {
        setValue('booking.commercialChecklist.declareInCIC.dueDate', moment(PODate).add(21, 'day').toDate())
      }
    }
  }, [PODate, getValues, setValue])

  return (
    <Card
      title="Commercial"
      rightElement={
        canEditBookingCommercialItem && (
          <Button size="sm" colorScheme="green" onClick={addCommercialOther}>
            Add
          </Button>
        )
      }
    >
      <Table>
        <Thead>
          <Tr>
            <Th>Item</Th>
            <Th>Content</Th>
            <Th>Due Date</Th>
            <Th>Attachment</Th>
            <Th>Remark</Th>
            <Th>Completed</Th>
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          <BookingCommercialItem
            title="Payment Terms"
            path="booking.commercialChecklist.paymentTerms"
            value={paymentTerms?.value}
            disabled={!canEditBookingCommercialItem}
          />
          <BookingCommercialItem
            title="Bond"
            path="booking.commercialChecklist.requireBond"
            value={requireBond?.value}
            type={'yesNoDropdown'}
            haveDueDate
            disabled={!canEditBookingCommercialItem}
          />
          <BookingCommercialItem
            title="Insurance"
            path="booking.commercialChecklist.insurance"
            value={insurance?.value}
            type={'yesNoDropdown'}
            haveDueDate
            disabled={!canEditBookingCommercialItem}
          />
          <BookingCommercialItem
            title="EPD"
            path="booking.commercialChecklist.openAccountForEPD"
            value={openAccountForEPD?.value}
            type={'yesNoDropdown'}
            haveDueDate
            disabled={!canEditBookingCommercialItem}
          />
          <BookingCommercialItem
            title="CIC PCFB Levy"
            path="booking.commercialChecklist.declareInCIC"
            value={declareInCIC?.value}
            type={'yesNoDropdown'}
            haveDueDate
            disabled={!canEditBookingCommercialItem}
          />
          <BookingCommercialItem
            title="Assignment consent for Sub-contract"
            path="booking.commercialChecklist.assignmentConsent"
            value={assignmentConsent?.value}
            type={'yesNoDropdown'}
            disabled={!canEditBookingCommercialItem}
          />
          {commercialOther.map((field, index) => (
            <BookingCommercialItem
              key={field.id}
              title="Assignment consent for Sub-contract"
              path={`booking.commercialChecklist.other.${index}`}
              value={assignmentConsent?.value}
              type={'yesNoDropdown'}
              isOther
              removeAction={() => {
                removeCommercialOther(index)
              }}
              disabled={!canEditBookingCommercialItem}
            />
          ))}
        </Tbody>
      </Table>
    </Card>
  )
}

export default BookingTabCommercialSection
